import { Component, ErrorHandler, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { disabled, form } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { firstValueFrom } from 'rxjs'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { AreYouSureDialog, AreYouSureDialogData } from '../../../_jaettu-angular/_components/are-you-sure.dialog'
import { arvonMuutokset, asetaArvo } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { KirjanpitajanNimitiedot } from '../../../_jaettu-lemonator/model/kirjanpitaja'
import { LLM_TOSITE_LUKU_PUTKEN_NIMI, LLM_TOSITE_LUKU_PUTKET, LlmTositeLukuPromptRow, LlmTositeLukuPutki } from '../../../_jaettu-lemonator/model/llm-tosite-luku-testaus'
import { luoPromptiversioVertailu, PromptiversioVertailu, VertailtavaPromptiversio } from '../prompti-versiovertailu.component'
import { callLlmTositeLukuTestaus } from './llm-tosite-luku-testaus.client'

@Component({
  selector: 'app-tosite-luku-prompti',
  templateUrl: './tosite-luku-prompti.component.html',
  styleUrls: ['./tosite-luku-prompti.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TositeLukuPromptiComponent implements OnInit {
  readonly putket = LLM_TOSITE_LUKU_PUTKET
  readonly putkenNimi = LLM_TOSITE_LUKU_PUTKEN_NIMI
  readonly putkenSelite: Record<LlmTositeLukuPutki, string> = {
    lemonator:
      'Tämä prompti on vain testipenkin käyttöön. Tuotanto käyttää koodiin kovakoodattua promptia, johon valittu versio kopioidaan käsin. Mallin vastausrakenne (päivämäärä, summa, toimittaja, y-tunnus/VAT, tunnuksen tyyppi, toimittajaAvain) on kovakoodattu eikä sitä muokata tässä.',
    lemonaid:
      'Tämä prompti on vain testipenkin käyttöön. Webin ja mobiilin tuotanto käyttää koodiin kovakoodattua promptia, johon valittu versio kopioidaan käsin. Mallin vastausrakenne (mm. päivämäärä, summa, valuutta, hyvitys, toimittaja, y-tunnus/VAT, maksukortti, lukukelvottomuus) ja vastauksen jälkikäsittely ovat koodissa eikä niitä muokata tässä.',
  }

  readonly tallennetaan = signal(false)
  readonly valittuPutki = signal<LlmTositeLukuPutki>('lemonator')
  putkiKentta = form(signal<LlmTositeLukuPutki>(this.valittuPutki()), polku => {
    disabled(polku, () => this.tallennetaan())
  })
  readonly uusin = signal<LlmTositeLukuPromptRow | null>(null)
  muokattuKentta = form(signal(''))
  readonly lataa = signal(false)

  readonly historia = signal<LlmTositeLukuPromptRow[]>([])
  readonly historiaLataa = signal(false)
  readonly naytaHistoria = signal(false)
  esikatselu: LlmTositeLukuPromptRow | null = null
  readonly esikatseluVertailu = signal<PromptiversioVertailu | null>(null)

  private readonly _kirjanpitajatMap = signal(new Map<string, KirjanpitajanNimitiedot>())

  constructor(
    private _firebase: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private _kirjanpitajaService: KirjanpitajaService,
  ) {
    arvonMuutokset(this.putkiKentta).subscribe(putki => this.vaihdaPutki(putki))
  }

  async ngOnInit() {
    const [, kirjanpitajatMap] = await Promise.all([this._lataaUusin(), firstValueFrom(this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable)])
    this._kirjanpitajatMap.set(kirjanpitajatMap)
  }

  get onMuutoksia(): boolean {
    return this.muokattuKentta().value() !== (this.uusin()?.prompt ?? '')
  }

  annaPaivittajanNimi(uid: string | null | undefined): string {
    if (!uid) {
      return '—'
    }
    const k = this._kirjanpitajatMap().get(uid)
    return k ? `${k.etunimi} ${k.sukunimi}` : uid
  }

  async vaihdaPutki(putki: LlmTositeLukuPutki) {
    const edellinen = this.valittuPutki()
    this.valittuPutki.set(putki)
    if (this.onMuutoksia) {
      const data: AreYouSureDialogData = { header: 'Tallentamattomia muutoksia', text: 'Promptiin on tallentamattomia muutoksia. Hylätäänkö ne?', rightAction: 'Hylkää' }
      const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
      if (!sure) {
        this.valittuPutki.set(edellinen)
        asetaArvo(this.putkiKentta, edellinen, { ilmoita: false })
        return
      }
    }
    // Tyhjennetään ennen hakua, ettei edellisen putken prompti jää editoriin ja tallennu uuden putken versioksi, jos haku epäonnistuu.
    this.uusin.set(null)
    asetaArvo(this.muokattuKentta, '')
    this.historia.set([])
    this.suljeEsikatselu()
    await this._lataaUusin()
    if (this.naytaHistoria()) {
      await this._lataaHistoria()
    }
  }

  async tallenna() {
    if (this.tallennetaan() || !this.muokattuKentta().value().trim()) {
      return
    }
    this.tallennetaan.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'tallennaPrompt', { prompt: this.muokattuKentta().value(), putki: this.valittuPutki() })
      if (vastaus.e || vastaus.versio == null) {
        this._snackbar.open(`Tallennus epäonnistui: ${vastaus.e ?? 'tuntematon virhe'}`, 'OK', { duration: 8000 })
        return
      }
      this._snackbar.open(`Uusi versio v${vastaus.versio} tallennettu`, 'OK', { duration: 5000 })
      await this._lataaUusin()
      if (this.naytaHistoria()) {
        await this._lataaHistoria()
      }
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Tallennus epäonnistui', 'OK', { duration: 8000 })
    } finally {
      this.tallennetaan.set(false)
    }
  }

  async toggleHistoria() {
    this.naytaHistoria.set(!this.naytaHistoria())
    if (this.naytaHistoria() && this.historia().length === 0) {
      await this._lataaHistoria()
    }
  }

  esikatsele(rivi: LlmTositeLukuPromptRow) {
    this.esikatselu = rivi
    this._paivitaVertailu()
  }

  suljeEsikatselu() {
    this.esikatselu = null
    this._paivitaVertailu()
  }

  async palautaVersio(versio: number) {
    if (this.tallennetaan()) {
      return
    }
    const data: AreYouSureDialogData = { header: 'Palauta versio', text: `Palauta versio v${versio} uudeksi versioksi?`, rightAction: 'Palauta' }
    const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!sure) {
      return
    }
    this.tallennetaan.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'palautaPromptVersio', { versio })
      if (vastaus.e || vastaus.versio == null) {
        this._snackbar.open(`Palautus epäonnistui: ${vastaus.e ?? 'tuntematon virhe'}`, 'OK', { duration: 8000 })
        return
      }
      this._snackbar.open(`Versio v${vastaus.versio} luotu vanhalla sisällöllä`, 'OK', { duration: 5000 })
      this.suljeEsikatselu()
      await this._lataaUusin()
      await this._lataaHistoria()
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Palautus epäonnistui', 'OK', { duration: 8000 })
    } finally {
      this.tallennetaan.set(false)
    }
  }

  private async _lataaUusin() {
    this.lataa.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haePromptUusin', { putki: this.valittuPutki() })
      if (vastaus.e) {
        this._snackbar.open('Promptin haku epäonnistui', 'OK', { duration: 8000 })
        return
      }
      this.uusin.set(vastaus.rivi)
      asetaArvo(this.muokattuKentta, vastaus.rivi?.prompt ?? '')
      this._paivitaVertailu()
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this.lataa.set(false)
    }
  }

  private async _lataaHistoria() {
    this.historiaLataa.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haePromptHistoria', { putki: this.valittuPutki() })
      if (vastaus.e) {
        this._snackbar.open('Historian haku epäonnistui', 'OK', { duration: 8000 })
        return
      }
      this.historia.set(vastaus.rivit)
      this._paivitaVertailu()
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this.historiaLataa.set(false)
    }
  }

  private _paivitaVertailu() {
    const muunna = (rivi: LlmTositeLukuPromptRow): VertailtavaPromptiversio => ({ versio: rivi.versio, paivitetty: rivi.paivitetty, teksti: rivi.prompt })
    this.esikatseluVertailu.set(this.esikatselu ? luoPromptiversioVertailu(this.esikatselu, this.historia(), this.uusin(), muunna) : null)
  }
}
